import ComponentBase, { ComponentBaseProps } from "../../../components/ComponentBase";
import Utils from "./common/Utils";
import GlobalSearchState, { 
	ContentSearchBlock, 
	KeyCode, 
	SearchBlockOperator, 
	SearchBlockOpType, 
	SearchBlockType, 
	GlobalSearchMode, 
	VocabColorPalette 
} from "../typings";
import SearchBlock from "./SearchBlock";

export interface SearchBoxProps extends ComponentBaseProps {
	readonly state: GlobalSearchState;
	readonly action: (event?: JQueryKeyEventObject, isShown?: boolean) => void;
	readonly onKeyDown?: (event: JQueryKeyEventObject) => void;
}

interface ISearchBlockNode {
	block: SearchBlock;
	idx: number;
}

interface ISiblingNodes {
	leftBlock?: ISearchBlockNode;
	rightBlock?: ISearchBlockNode;
}

interface IInsertModeProps extends ISiblingNodes {
	nextInput?: JQuery;
	insertContainer?: JQuery;
	insertInput?: JQuery;
}

interface PhraseUpdateMetaData {
	leftSubPhrase?: SearchBlock[];
	rightSubPhrase?: SearchBlock[];
	subPhrase?: SearchBlock[];
	indexInSubPhrase: number;
	prevPhraseBlock?: SearchBlock;
	nextPhraseBlock?: SearchBlock;
}

export default class SearchBox extends ComponentBase<SearchBoxProps> {
	private input: JQuery;
	private inputContainer: JQuery;
	private mainInput: JQuery;
	private isInsertMode = false;
	private insertModeProps: IInsertModeProps = {};
	private nonEmptyPlaceholderMessage = false;
	protected blocks: SearchBlock[] = [];
	public selectedOption: JQuery;
	private previousPlaceholder: JQuery;

	public get defaultTextBlock(): ContentSearchBlock {
		return {
			type: SearchBlockType.FreeText,
			text: this.text,
			opType: null,
			vocab: null,
			descriptor: null
		};
	}

	constructor(props: SearchBoxProps) {
		super(props);
	}

	public get inputField(): JQuery {
		return this.isEditMode ? this.editableBlock.input : this.input;
	}

	public get isEmpty(): boolean {
		return !(this.blocks?.length || this.inputField?.val());
	}

	public get hasBlocks(): boolean {
		return (this.blocks?.length || 0) > 0;
	}

	public get text(): string {
		return this.inputField?.val();
	}

	public get needClear(): boolean {
		return this.blocks?.length || this.input?.val();
	}

	public get terms(): ContentSearchBlock[] {
		return this.blocks?.map(block => block.blockTerm);
	}

	public get searchBlocks(): SearchBlock[] {
		return this.blocks;
	}

	public get isInInsertMode(): boolean {
		return this.isInsertMode || false;
	}

	public validateOperator(operator: SearchBlockOperator): boolean {
		if (!operator) {
			return false;
		} else if (Utils.isBoolOperator(operator.type) && !this.canAddBoolOperator) {
			return false;
		} else if (operator.type === SearchBlockOpType.RightParens) {
			if (!this.canAddRightParens) {
				return false;
			}
		} else if (operator.type === SearchBlockOpType.LeftParens) {
			if (!this.canAddLeftParens) {
				return false;
			}
		}
		return true;
	}

	public get canAddBoolOperator(): boolean {
		const nNodes = this.getSiblingBlocks();
		const result = nNodes.leftBlock &&
			(!nNodes.rightBlock || !nNodes.rightBlock?.block?.isBoolOperator) &&
			(!nNodes.leftBlock?.block?.isBoolOperator && !nNodes.leftBlock?.block?.isOpeningBracket);
		return result;
	}

	public get currentBlockIndex(): number {
		const prevBlock = this.prevBlock(this.input);
		return prevBlock ? prevBlock.idx + 1 : 0;
	}

	public get isEditMode(): boolean {
		return this.blocks.some(block => block.isEditMode);
	}

	private get editableBlock(): SearchBlock {
		return this.blocks.filter(block => block.isEditMode)[0];
	}

	private get lastBlock(): SearchBlock {
		const lastBlock = this.getSiblingBlocks()?.leftBlock;
		return lastBlock ? this.blocks[lastBlock.idx] : null;
	}

	public get canAddLeftParens(): boolean {
		const lastBlock = this.lastBlock;
		return !lastBlock
			|| lastBlock.isBoolOperator || lastBlock.opType === SearchBlockOpType.LeftParens || lastBlock === this.blocks[this.blocks.length - 1];
	}
	
	public get canAddRightParens(): boolean {
		let count = 0;
		const lastBlock = this.lastBlock;

		this.blocks.forEach(block => {
			if (block.opType === SearchBlockOpType.LeftParens) {
				count++;
			}

			if (block.opType === SearchBlockOpType.RightParens) {
				count--;
			}
		});

		return this.blocks.length
			&& count > 0
			&& lastBlock 
			&& (!lastBlock.isOperator || lastBlock.opType === SearchBlockOpType.RightParens);
	}

	public get searchPhraseLog(): string {
		const blocks = this.terms.length ? this.terms : (this.text ? [this.defaultTextBlock] : []);
		return Utils.termsToLogString(blocks);
	}

	public get searchParams(): string {
		let params = "";
		const propDelim = encodeURIComponent(GlobalSearchState.propDelim);
		const blockDelim = encodeURIComponent(GlobalSearchState.blockDelim);
		const blocks = this.terms.length ? this.terms : (this.text ? [this.defaultTextBlock] : []);
		$.each(blocks, (index, value) => {
			if (params.length > 0) {
				params += blockDelim;
			}

			params += [
					value.type,
					encodeURIComponent(value.text),
					value.descriptor?.descriptorId ?? 0,
					value.descriptor?.vocabularyId ?? 0,
					value.opType ?? -1]
				.join(propDelim);

		});

		return params;
	}

	public get isSemanticSearchMode(): boolean {
		return this.props.state.searchMode === GlobalSearchMode.SemanticSearch;
	}

	public get isDefaultSearchMode(): boolean {
		return this.props.state.searchMode === GlobalSearchMode.Default;
	}

	protected renderInternal(): JQuery {
		const blocks = this.props.state.contentSearchBlockList;
		const nonEmptySearchBox = ((blocks && blocks.length) || 0) > 0;
		this.nonEmptyPlaceholderMessage = nonEmptySearchBox;
		this.input = this.renderInput(nonEmptySearchBox);
		this.inputContainer = $("<div>")
			.addClass("rup-global-search__chip-insertion globalSearch__chip--mainInput");
		this.inputContainer.append(this.input);

		this.containerInternal = $("<div>")
			.addClass("rup-global-search__box")
			.append(this.inputContainer);

		if (blocks?.length) {
			if (!this.isDefaultSearchMode) {
				blocks.forEach(block => this.addChip(block));
			} else {
				this.input.val(blocks[0].text);
			}
		}

		return this.containerInternal;
	}

	private insertModeOn(input: JQuery, text: string): void {
		const insertContainer = $("<div>")
			.addClass("rup-global-search__chip-insertion globalSearch__chip--insertion");
		insertContainer.insertBefore(this.getBlockContainer(input));

		const newInput = this.renderInsertInput();
		insertContainer.append(newInput);

		this.mainInput = this.input;
		this.input = newInput;
		this.isInsertMode = true;
		this.insertModeProps = {
			leftBlock: this.prevBlock(input),
			rightBlock: this.nextBlock(input),
			nextInput: input,
			insertContainer: insertContainer,
			insertInput: newInput,
		};
		if (text && text.length) {
			this.input.val(text);
		}
		this.input.focus();
		if (this.props && this.props.action) {
			this.props.action();
		}
	}

	private addBlock(term: ContentSearchBlock, nodes: ISiblingNodes, baseElement: JQuery): void {
		const insertion = this.buildBlocksInsertion(term, nodes);
		if (insertion && insertion.length) {
			insertion.forEach((b) => {
				this.blocks.splice(b.idx, 0, b.block);
				this.renderBlock(b.block, baseElement);
			});
		}
	}

	public insertModeOff(): void {
		if (!this.isInsertMode) {
			return;
		}

		const term = this.defaultTextBlock;

		if (this.props.action) {
			this.props.action();
		}

		this.isInsertMode = false;
		if (this.mainInput) {
			this.input = this.mainInput;
			this.mainInput = null;
		}

		if ($(this.insertModeProps.nextInput).length) {
			this.insertModeProps.nextInput.focus();
		} else {
			let focusOn = this.containerInternal.find("div.rup-global-search__chip-insertion > input");
			if (focusOn.length) {
				focusOn[0].focus();
			} else {
				focusOn = this.containerInternal.find("div.rup-global-search__chip-placeholder > input");
				if (focusOn.length) {
					focusOn[focusOn.length - 1].focus();
				}
			}
		}

		if (term?.text?.length) {
			this.addBlock(term, this.insertModeProps, this.insertModeProps.insertContainer);
		}

		this.insertModeProps?.insertInput?.remove();
		this.insertModeProps?.insertContainer?.remove();
		this.insertModeProps = {};
	}

	private updateInputWidth(input: HTMLInputElement): void {
		if (!input) {
			return;
		}

		const newSize = (input?.value?.length || 0) + 1;
		input.setAttribute("size", Math.max(2, newSize).toString());
	}

	private renderInsertInput(): JQuery {
		const input = $("<input>")
			.addClass("rup-input globalSearch__input--insertion")
			.attr({
				autocomplete: "off",
				inputmode: "search",
				role: "combobox",
				"aria-live": "polite",
				"aria-label": "Text input",
				"size": "2",
				"data-test-id": "search-input"
			})
			.on("keydown", (event) => this.onKeyDown(event))
			.on("input", (event) => {
				this.updateInputWidth(event.currentTarget as HTMLInputElement);
				this.props.action();
			})
			.on("blur", () => this.insertModeOff());

		return input;
	}

	private renderInput(nonEmptySearchBoxMessage = false): JQuery {
		const placeholderMessage = nonEmptySearchBoxMessage
			? this.props.state.emptyMessageContinueEditing
			: this.props.state.emptyMessage;
		return $("<input>")
			.addClass("rup-input")
			.attr({
				placeholder: placeholderMessage,
				autocomplete: "off",
				inputmode: "search",
				role: "combobox",
				"aria-live": "polite",
				"aria-label": "Text input",
				"data-test-id": "search-input"
			})
			.on("keydown", (event) => this.onKeyDown(event))
			.on("input", (event) => {
				this.toggleMainInputPlaceholderText();
				this.props.action(event);
			})
			.on("blur", (event) => this.placeholderInputLostFocus($(event.currentTarget)));
	}

	private toggleMainInputPlaceholderText(): void {
		if (this.blocks?.length) {
			this.turnOnNonEmptySearchBoxPlaceholderText();
		} else {
			this.turnOnEmptySearchBoxPlaceholderText();
		}
	}

	private setInputPlaceholder(placeholderText: string): void {
		this.nonEmptyPlaceholderMessage = this.props.state.emptyMessageContinueEditing === placeholderText;
		const input = $(this.containerInternal)
			.find("div.globalSearch__chip--mainInput")
			.find("input.rup-input");
		input
			.attr("placeholder", placeholderText);
		if (this.nonEmptyPlaceholderMessage && placeholderText?.length) {
			input.attr("size", placeholderText.length.toString());
		} else {
			input.removeAttr("size");
		}
	}

	private turnOnNonEmptySearchBoxPlaceholderText(): void {
		this.setInputPlaceholder(this.props.state.emptyMessageContinueEditing);
	}

	public turnOnEmptySearchBoxPlaceholderText(): void {
		this.setInputPlaceholder(this.props.state.emptyMessage);
	}

	private processEnterPressed(e: JQueryKeyEventObject & JQueryInputEventObject): boolean {
		if (this.selectedOption) {
			this.selectedOption.click();
			this.toggleMainInputPlaceholderText();
			return false;
		}

		const inputVal = normalizeText(this.input.val());
		if (inputVal !== '' && IsNullOrWhitespaces(inputVal)) {
			this.input.focus();
			e.stopPropagation();
			return true;
		}

		const eBlocks = (this.blocks || []).filter(b => b.isEditMode);
		if (this.addTextBlockIfNecessary()) {
			let moveFocusAt = null;
			if (eBlocks?.length) {
				const idxBlock = this.blocks.indexOf(eBlocks[0]);
				if (this.blocks[idxBlock + 1]) {
					moveFocusAt = this.blocks[idxBlock + 1].placeholderInput;
				}
			}
			this.props.action();
			if (moveFocusAt) {
				moveFocusAt.focus();
			} else {
				this.input.focus();
			}
		} else {
			if (this.isEditMode) {
				this.editableBlock.editModeOff(e as JQueryEventObject);
				return true;
			}
			this.props.onKeyDown(e);
		}

		if (this.isInsertMode) {
			this.insertModeOff();
		}

		this.toggleMainInputPlaceholderText();
		return false;
	}

	private onKeyDown(e: JQueryKeyEventObject & JQueryInputEventObject): void {
		const code = e.key;
		const input = this.inputField;

		if (code === KeyCode.Enter) {
			e.preventDefault();
			this.processEnterPressed(e);
		}
		else if (code === KeyCode.Backspace) {
			if (!IsNullOrEmpty(input.val())) {
				return;
			}

			const currentInput = $(e.target);
			const block = this.prevBlock(currentInput)?.block;
			if (block) {
				this.removeBlock(block);
			} else {
				currentInput.focus();
			}
		}
		else if (code === KeyCode.ArrowDown) {
			e.preventDefault();
			this.props.onKeyDown(e);
			input.focus();
		}
		else if (code === KeyCode.ArrowUp) {
			e.preventDefault();
			this.props.onKeyDown(e);
			input.focus();
		}
		else if (code === KeyCode.ArrowLeft) {
			const prev = this.getBlockContainer($(e.target))
				.prevAll("div.rup-global-search__chip-insertion, div.rup-global-search__chip-placeholder")
				.first();
			if (prev && IsNullOrEmpty(input.val())) {
				prev.find("input").focus();
				e.preventDefault();
			}
		}
		else if (code === KeyCode.ArrowRight) {
			const next = this.getBlockContainer($(e.target))
				.nextAll("div.rup-global-search__chip-insertion, div.rup-global-search__chip-placeholder")
				.first();
			if (next && IsNullOrEmpty(input.val())) {
				$(next).find("input").focus();
				e.preventDefault();
			}
		}
		else if (code === KeyCode.Escape) {
			if (this.selectedOption) {
				this.selectedOption.toggleClass("rup-global-search__selectable-option--active globalSearch__button--active globalSearch__button");
				this.selectedOption = null;
			}

			if (this.isEditMode) {
				if (this.editableBlock.resetChanges(e as JQueryEventObject)) {
					e.stopPropagation();
					this.restoreLostPlaceholderFocus();
					return;
				}
				this.input.focus();
			}

			if (this.isInsertMode) {
				this.insertModeOff();
				e.preventDefault();
			}
		}
		else if (code && code.length === 1) {
			if (!this.nonEmptyPlaceholderMessage) {
				this.turnOnNonEmptySearchBoxPlaceholderText();
			}

			if ($(e.target).hasClass("globalSearch__placeholder")) {
				e.preventDefault();
				this.insertModeOn($(e.target), code);
			}
		}
	}

	private getBlockContainer(block: JQuery): JQuery {
		if (!block) {
			return null;
		}
		if (block.is("input") && block.hasClass("rup-input-placeholder")) {
			return block.parent("div.rup-global-search__chip-placeholder");
		}
		if (block.is("div")) {
			return block;
		}
		return block.parent("div");
	}

	private prevBlock(block: JQuery): ISearchBlockNode {
		const baseBlock = this.getBlockContainer(block);
		const prev = baseBlock.prevAll(".rup-global-search__chip:first");
		if (!prev || !prev.length) {
			return null;
		}

		return (this.blocks || [])
			.map((blk, idx) => ({ block: blk, idx: idx }))
			.filter(b => b.block.container.is(prev))[0] || null;
	}

	private nextBlock(block: JQuery): ISearchBlockNode {
		const baseBlock = this.getBlockContainer(block);
		const next = baseBlock.nextAll(".rup-global-search__chip:first");
		if (!next || !next.length) {
			return null;
		}

		return (this.blocks || [])
			.map((blk, idx) => ({ block: blk, idx: idx }))
			.filter(b => b.block.container.is(next))[0] || null;
	}

	public addTextBlockIfNecessary(): boolean {
		if (this.text && !IsNullOrWhitespaces(normalizeText(this.text)) && !this.isDefaultSearchMode) {
			const operator = Utils.getOperator(this.text);
			const textBlock : ContentSearchBlock = this.validateOperator(operator)? 
					this.getOperatorTerm(operator) : this.defaultTextBlock;
			this.addChip(textBlock);
			return true;
		}
		return false;
	}

	public getSiblingBlocks(): ISiblingNodes {
		if (!this.blocks || !this.blocks.length) {
			return {
				leftBlock: null,
				rightBlock: null,
			};
		}

		if (this.isInsertMode) {
			return this.insertModeProps as ISiblingNodes;
		}

		if (this.isEditMode) {
			for (let i = 0; this.blocks.length; i += 1) {
				if (this.blocks[i].isEditMode) {
					const leftBlock = this.blocks[i - 1]
						? { block: this.blocks[i - 1], idx: i - 1 }
						: null;
					const rightBlock = this.blocks[i + 1]
						? { block: this.blocks[i + 1], idx: i + 1 }
						: null;
					return {
						leftBlock: leftBlock,
						rightBlock: rightBlock,
					};
				}
			}
		}

		return {
			leftBlock: {
				block: this.blocks[this.blocks.length - 1],
				idx: this.blocks.length - 1,
			},
			rightBlock: null,
		};
	}

	private buildRightBlocks(term: ContentSearchBlock, rightBlock: SearchBlock): SearchBlock[] {
		if (!term || !rightBlock) {
			return [];
		}

		if (term.type === SearchBlockType.Operator && term.opType !== SearchBlockOpType.RightParens) {
			return [];
		}

		if (rightBlock.isOperator && rightBlock.opType !== SearchBlockOpType.LeftParens) {
			return [];
		}

		return [this.getOperatorBlock(this.getDefaultOperatorTerm())];
	}

	private buildLeftBlocks(term: ContentSearchBlock, leftBlock: SearchBlock): SearchBlock[] {
		if (!term || !leftBlock) {
			return [];
		}

		if (term.type === SearchBlockType.Operator && term.opType !== SearchBlockOpType.LeftParens) {
			return [];
		}

		if (leftBlock.isOperator && leftBlock.opType !== SearchBlockOpType.RightParens) {
			return [];
		}

		return [this.getOperatorBlock(this.getDefaultOperatorTerm())];
	}

	private buildBlocksInsertion(term: ContentSearchBlock, nodes: ISiblingNodes): ISearchBlockNode[] {
		if (!nodes || !term) {
			return [];
		}

		if (!nodes.leftBlock && !nodes.rightBlock) {
			return [{
				block: (term.type === SearchBlockType.Operator) ? this.getOperatorBlock(term) : this.getVocabBlock(term),
				idx: 0,
			}];
		}

		const blockStartIndex: number = (!nodes.leftBlock && nodes.rightBlock)
			? 0
			: nodes.leftBlock.idx + 1;

		const leftBlock = nodes?.leftBlock?.block || null;
		const rightBlock = nodes?.rightBlock?.block || null;
		const termBlock = (term.type !== SearchBlockType.Operator)
			? this.getVocabBlock(term)
			: this.getOperatorBlock(term);

		if (!leftBlock && rightBlock) {
			// insert before
			if (termBlock.isBoolOperator) {
				return [];
			}
			const rightBlocks = this.buildRightBlocks(term, rightBlock);
			return [termBlock].concat(rightBlocks || [])
				.map((b, i) => ({ block: b, idx: blockStartIndex + i }));
		}

		if (leftBlock && !rightBlock) {
			// append after
			const leftBlocks = this.buildLeftBlocks(term, leftBlock);
			return leftBlocks.concat([termBlock])
				.map((b, i) => ({ block: b, idx: blockStartIndex + i }));
		}

		if (leftBlock && rightBlock) {
			// insert between
			if (termBlock.isSpecialOperator) {
				let canAddLeftBracket = false;
				let canAddRightBracket = false;
				if (term.opType === SearchBlockOpType.LeftParens) {
					canAddLeftBracket = ((leftBlock.isOperator && !leftBlock.isSpecialOperator) ||
						(leftBlock.isSpecialOperator && leftBlock.opType === SearchBlockOpType.LeftParens)) &&
						(!rightBlock.isOperator || rightBlock.opType === SearchBlockOpType.LeftParens);
				}
				if (term.opType === SearchBlockOpType.RightParens) {
					canAddRightBracket = ((rightBlock.isOperator && !rightBlock.isSpecialOperator) ||
						(rightBlock.isSpecialOperator && rightBlock.opType === SearchBlockOpType.RightParens)) &&
						(!leftBlock.isOperator || leftBlock.opType === SearchBlockOpType.RightParens);
				}
				if (canAddLeftBracket || canAddRightBracket) {
					return [{ block: termBlock, idx: blockStartIndex }];
				}
				return [];
			}

			if (termBlock.isBoolOperator) {
				if (leftBlock.isBoolOperator || rightBlock.isBoolOperator) {
					return [];
				}
			}

			const rightBlocks = this.buildRightBlocks(term, rightBlock);
			const leftBlocks = this.buildLeftBlocks(term, leftBlock);

			const builtBlocks = leftBlocks.concat([termBlock], rightBlocks);
			return (builtBlocks || [])
				.map((b, i) => ({ block: b, idx: blockStartIndex + i }));
		}

		return [];
	}

	public addChip(term: ContentSearchBlock): void {
		const input = this.isEditMode ? this.editableBlock.input : this.input;

		if (term.text && (term.type !== SearchBlockType.Operator)) {
			term.text = normalizeText(term.text);
		}

		if (this.isDefaultSearchMode) {
			this.blocks = [];
			return;
		}

		if (this.isEditMode) {
			this.editableBlock.replaceBlock(term, this.getBlockColors(term));
		} else {
			const siblingBlocks = this.getSiblingBlocks();
			this.addBlock(term, siblingBlocks, this.insertModeProps.insertContainer);
			if (!siblingBlocks.rightBlock) {
				this.previousPlaceholder = null;
			}
		}

		input?.val("");
		this.props.action();
		input?.focus();
	}

	private getOperatorTerm(operator: SearchBlockOperator): ContentSearchBlock {
		return {
			text: operator.text, 
			opType: operator.type,
			type: SearchBlockType.Operator
		};
	}

	private getDefaultOperatorTerm(): ContentSearchBlock {
		return {
			text: "AND",
			opType: SearchBlockOpType.AND,
			type: SearchBlockType.Operator
		};
	}

	private renderBlock(block: SearchBlock, baseElement: JQuery = null) {
		const baseRender = baseElement || this.inputContainer;
		block.render().insertBefore(baseRender);
		block.animateFadeIn();
		block.addChipPlaceholder();
		this.toggleMainInputPlaceholderText();
	}

	private getBlockColors(term: ContentSearchBlock): VocabColorPalette {
		return this.props.state.vocabColorPalette[term.descriptor?.vocabularyId];
	}

	private getOperatorBlock(term: ContentSearchBlock): SearchBlock {
		const block = new SearchBlock({
			onClear: this.removeBlock.bind(this),
			onKeyDown: this.onKeyDown.bind(this),
			term: term,
			onPlaceholderLostFocus: this.placeholderInputLostFocus.bind(this),
			operatorUpdatedHandler: this.restoreLostPlaceholderFocus.bind(this),
		});

		return block;
	}

	protected addOperatorBlock(term: ContentSearchBlock, insertAt = -1): SearchBlock {
		const opBlock = this.getOperatorBlock(term);

		if (insertAt >= 0) {
			this.blocks.splice(insertAt, 0, opBlock);
		} else {
			this.blocks.push(opBlock);
		}
		
		return opBlock;
	}

	private getVocabBlock(term: ContentSearchBlock): SearchBlock {
		return new SearchBlock({
			vocabColorPalette: this.getBlockColors(term),
			onClear: this.removeBlock.bind(this),
			onKeyDown: (event) => this.onKeyDown(event),
			renderInput: this.renderInput.bind(this),
			addTextBlockIfNecessary: this.addTextBlockIfNecessary.bind(this),
			term: term,
			onPlaceholderLostFocus: this.placeholderInputLostFocus.bind(this),
			operatorUpdatedHandler: this.restoreLostPlaceholderFocus.bind(this),
		});
	}

	private buildPhraseDataToDelete(blocks: SearchBlock[] = [], blockToDelete: SearchBlock, indexToDelete: number): PhraseUpdateMetaData {
		let openingBracketIndex = -1;

		for (let i = indexToDelete - 1; i >= 0; i -= 1) {
			if (this.blocks[i].isOpeningBracket) {
				openingBracketIndex = i;
				break;
			}
		}

		let closingBracketIndex = blocks.length;

		if (openingBracketIndex !== -1) {
			for (let i = indexToDelete + 1; i < blocks.length; i += 1) {
				if (this.blocks[i].isClosingBracket) {
					closingBracketIndex = i;
					break;
				}
			}
		}

		const subPhrase = openingBracketIndex > -1
			? blocks.slice(openingBracketIndex, closingBracketIndex + 1)
			: blocks.slice();
		const blockToDeleteIdx = subPhrase.indexOf(blockToDelete);

		return {
			leftSubPhrase: openingBracketIndex > -1 ? blocks.slice(0, openingBracketIndex) : [],
			subPhrase: subPhrase,
			rightSubPhrase: (closingBracketIndex < blocks.length) ? blocks.slice(closingBracketIndex + 1, blocks.length) : [],
			indexInSubPhrase: blockToDeleteIdx,
			prevPhraseBlock: blockToDeleteIdx > 0 ? subPhrase[blockToDeleteIdx - 1] : null,
			nextPhraseBlock: blockToDeleteIdx < subPhrase.length - 1 ? subPhrase[blockToDeleteIdx + 1] : null,
		};
	}

	private removeBlockFromPhrase(phraseMetaData: PhraseUpdateMetaData, idxToDelete: number): void {
		if (idxToDelete < 0) {
			return;
		}
		const phrase = phraseMetaData.subPhrase;
		const leftPart = phrase.slice(0, idxToDelete);
		const rightPart = phrase.slice(Math.min(idxToDelete + 1, phrase.length), phrase.length);
		phraseMetaData.subPhrase = leftPart.concat(rightPart);
	}

	private fixBoolOperators(phraseMetaData: PhraseUpdateMetaData): void {
		const leftBlock = phraseMetaData.prevPhraseBlock;
		const rightBlock = phraseMetaData.nextPhraseBlock;

		if (!leftBlock || leftBlock.isOpeningBracket) {
			if (rightBlock && rightBlock.isBoolOperator) {
				const rightBlockIdx = (phraseMetaData.subPhrase || []).indexOf(rightBlock);
				this.removeBlockFromPhrase(phraseMetaData, rightBlockIdx);
			}
		} else {
			if (leftBlock.isBoolOperator && (!rightBlock || rightBlock.isBoolOperator || rightBlock.isClosingBracket)) {
				const leftBlockIdx = (phraseMetaData.subPhrase || []).indexOf(leftBlock);
				this.removeBlockFromPhrase(phraseMetaData, leftBlockIdx);
			}
		}
	}

	private fixBrackets(phraseMetaData: PhraseUpdateMetaData): void {
		const phrase = phraseMetaData.subPhrase;
		const phraseLen = phrase.length;

		if ((phraseLen === 3) || (phraseLen === 2)) {
			if (phrase[0].isOpeningBracket && phrase[phraseLen - 1].isClosingBracket) {
				this.removeBlockFromPhrase(phraseMetaData, phraseLen - 1);
				this.removeBlockFromPhrase(phraseMetaData, 0);
			}
		}

		if (phraseLen === 1 && phrase[0].isOpeningBracket) {
			this.removeBlockFromPhrase(phraseMetaData, 0);
		}
	}

	private getUpdatedSearchBlockList(phraseMetaData: PhraseUpdateMetaData): SearchBlock[] {
		const subPhraseLength = phraseMetaData.subPhrase?.length || 0;
		if (subPhraseLength === 0) {
			const leftPhrase = phraseMetaData.leftSubPhrase || [];
			const leftPhraseLength = leftPhrase?.length || 0;
			if (leftPhraseLength && leftPhrase[leftPhraseLength - 1].isBoolOperator) {
				return leftPhrase.slice(0, leftPhraseLength - 1).concat(phraseMetaData.rightSubPhrase.slice());
			}
		}

		return (phraseMetaData.leftSubPhrase || [])
			.concat(phraseMetaData.subPhrase || [])
			.concat(phraseMetaData.rightSubPhrase || []);
	}

	private isValidToDelete(block: SearchBlock, blockIndex: number): boolean {
		return !block.isBoolOperator ||
			(block.isBoolOperator && (!this.blocks[blockIndex + 1] || this.blocks[blockIndex + 1].isClosingBracket));
	}
	
	private removeBlock(block: SearchBlock): void {
		const blockIndex = this.blocks.indexOf(block);
		const blockIndexDiff = this.blocks.length - 1 - blockIndex;
		const rightBlock = this.blocks[blockIndex + 1];

		if (!this.isValidToDelete(block, blockIndex)) {
			this.toggleMainInputPlaceholderText();
			return;
		}

		const metaData = this.buildPhraseDataToDelete(this.blocks, block, blockIndex);
		this.removeBlockFromPhrase(metaData, metaData.indexInSubPhrase);
		this.fixBoolOperators(metaData);
		this.fixBrackets(metaData);

		const updatedPhrase = this.getUpdatedSearchBlockList(metaData);
		for (const block of this.blocks) {
			if (updatedPhrase.indexOf(block) < 0) {
				block.clear();
			}
		}

		this.blocks = updatedPhrase;
		this.toggleMainInputPlaceholderText();

		if (rightBlock && (this.blocks?.length > 1)) {
			let moveToIndex = this.blocks.indexOf(rightBlock);
			if (moveToIndex < 0) {
				moveToIndex = this.blocks.length - blockIndexDiff;
			}
			if (moveToIndex >= 0) {
				let focusAtBlock = this.blocks[moveToIndex];
				if (!focusAtBlock.isOperator) {
					if (this.blocks[moveToIndex - 1]) {
						focusAtBlock = this.blocks[moveToIndex - 1];
					} else if (this.blocks[moveToIndex + 1]) {
						focusAtBlock = this.blocks[moveToIndex + 1];
					}
				}
				$(focusAtBlock.placeholderInput).focus();
				return;
			}
		}

		this.inputField.focus();
	}

	private placeholderInputLostFocus(input: JQuery): void {
		this.previousPlaceholder = input;
	}

	public restoreLostPlaceholderFocus(): void {
		const prevInput = this.previousPlaceholder && $(this.previousPlaceholder)[0];
		if (prevInput) {
			prevInput.focus();
			return;
		}
		this.input.focus();
	}
}

